import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import {
  BellOff,
  EllipsisVertical,
  MessageSquare,
  Plus,
  Send,
  Settings,
  TriangleAlert,
  User,
} from "@thuthi/ui/icons";
import { Alert, AlertDescription, AlertTitle } from "@thuthi/ui/components/alert";
import { Button } from "@thuthi/ui/components/button";
import { Separator } from "@thuthi/ui/components/separator";
import { Spinner } from "@thuthi/ui/components/spinner";
import { Sheet, SheetContent, SheetTitle } from "@thuthi/ui/components/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@thuthi/ui/components/tooltip";
import SubscribeDialog from "./SubscribeDialog";
import TopicAvatar from "./TopicAvatar";
import { topicDisplayName, topicUrl } from "../app/utils";
import routes from "./routes";
import { ConnectionState } from "../app/ConnectionManager";
import notifier from "../app/Notifier";
import config from "../app/config";
import session from "../app/Session";
import accountApi from "../app/AccountApi";
import { SubscriptionPopup } from "./SubscriptionPopup";
import { useNotificationPermissionListener } from "./hooks";
import type { Subscription } from "../app/types";

const navWidth = 240; // Tailwind w-60 (Messaging.jsx 등이 Navigation.width 로 참조)

// 사이드바는 부품함의 sidebar 토큰(--sidebar*)을 따른다 — 라이트/다크·세이지 테마와 함께 바뀐다(2026-10-05, 예전엔 #111315 고정).
const sidebarClasses = "w-60 flex-col bg-sidebar text-sidebar-foreground";

// MUI ListItemButton(+selected 스타일, 왼쪽 인디케이터) 대응 공통 클래스
const navItemClasses = (selected: boolean | null | undefined) =>
  [
    "relative flex min-h-[46px] w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-left text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-primary/50",
    selected
      ? "bg-sidebar-primary/20 font-bold text-sidebar-foreground shadow-none hover:bg-sidebar-primary/25 before:absolute before:left-0 before:top-1/2 before:h-[22px] before:w-[3px] before:-translate-y-1/2 before:rounded-lg before:bg-sidebar-primary before:content-['']"
      : "text-sidebar-foreground/70 hover:bg-sidebar-foreground/10",
  ].join(" ");

interface NavigationProps {
  subscriptions?: Subscription[];
  selectedSubscription?: Subscription | null;
  mobileDrawerOpen: boolean;
  onMobileDrawerClose?: () => void;
  onPublishMessageClick: () => void;
}

const Navigation = (props: NavigationProps) => {
  const navigate = useNavigate();
  const [subscribeDialogKey, setSubscribeDialogKey] = useState(0);
  const [subscribeDialogOpen, setSubscribeDialogOpen] = useState(false);

  const handleSubscribeReset = () => {
    setSubscribeDialogOpen(false);
    setSubscribeDialogKey((prev) => prev + 1);
  };

  const handleSubscribeSubmit = (subscription: Subscription) => {
    console.log(`[Navigation] New subscription: ${subscription.id}`, subscription);
    handleSubscribeReset();
    navigate(routes.forSubscription(subscription), { replace: routes.isTopLevel(window.location.pathname) });
  };

  // 구독 다이얼로그는 반드시 드로어(Sheet) "밖"에서 렌더링해야 한다.
  // 드로어 안(NavList)에 두면 모바일에서 구독 버튼이 드로어를 닫는 순간
  // Radix 가 드로어 내용을 언마운트하면서 다이얼로그도 함께 사라진다.
  const handleSubscribeClick = () => {
    props.onMobileDrawerClose?.();
    setSubscribeDialogOpen(true);
  };

  const navigationList = <NavList {...props} onSubscribeClick={handleSubscribeClick} />;
  return (
    <nav role="navigation" className="shrink-0 sm:w-60">
      {/* Mobile drawer; only shown if menu icon clicked (mobile open) and display is small */}
      <Sheet open={props.mobileDrawerOpen} onOpenChange={(open) => !open && props.onMobileDrawerClose?.()}>
        <SheetContent
          side="left"
          aria-describedby={undefined}
          className={`${sidebarClasses} gap-0 border-0 p-0 sm:hidden [&>button]:hidden`}
        >
          <SheetTitle className="sr-only">ntfy</SheetTitle>
          <div role="menubar" className="flex min-h-0 flex-1 flex-col">
            {navigationList}
          </div>
        </SheetContent>
      </Sheet>
      {/* Desktop: permanent drawer 대응 고정 사이드바 */}
      <div
        role="menubar"
        className={`${sidebarClasses} fixed inset-y-0 left-0 z-40 hidden border-r border-sidebar-foreground/10 sm:flex`}
      >
        {navigationList}
      </div>
      <SubscribeDialog
        key={`subscribeDialog${subscribeDialogKey}`} // Resets dialog when canceled/closed
        open={subscribeDialogOpen}
        subscriptions={props.subscriptions}
        onCancel={handleSubscribeReset}
        onSuccess={handleSubscribeSubmit}
      />
    </nav>
  );
};
Navigation.width = navWidth;

interface NavListProps extends NavigationProps {
  onSubscribeClick: () => void;
}

const NavList = (props: NavListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const handleNavigate = (route: string) => {
    props.onMobileDrawerClose?.();
    navigate(route, { replace: routes.isTopLevel(route) && routes.isTopLevel(window.location.pathname) });
  };

  const handleAccountClick = () => {
    accountApi.sync(); // Dangle!
    handleNavigate(routes.account);
  };

  const handlePublishMessageClick = () => {
    props.onMobileDrawerClose?.();
    props.onPublishMessageClick();
  };

  const showSubscriptionsList = (props.subscriptions?.length ?? 0) > 0;
  const showNotificationPermissionRequired = useNotificationPermissionListener(() => notifier.notRequested());
  const showNotificationPermissionDenied = useNotificationPermissionListener(() => notifier.denied());
  const showNotificationIOSInstallRequired = notifier.iosSupportedButInstallRequired();
  const showNotificationBrowserNotSupportedBox = !showNotificationIOSInstallRequired && !notifier.browserSupported();
  const showNotificationContextNotSupportedBox = notifier.browserSupported() && !notifier.contextSupported(); // Only show if notifications are generally supported in the browser

  const alertVisible =
    showNotificationPermissionRequired ||
    showNotificationPermissionDenied ||
    showNotificationIOSInstallRequired ||
    showNotificationBrowserNotSupportedBox ||
    showNotificationContextNotSupportedBox;

  return (
    <>
      <div className="flex min-h-[94px] shrink-0 items-center gap-2.5 border-b border-sidebar-foreground/10 px-[18px]">
        <div
          aria-hidden
          className="grid size-9 shrink-0 place-items-center rounded-lg bg-sidebar-primary font-extrabold text-sidebar-primary-foreground"
        >
          n
        </div>
        <div className="min-w-0">
          <p className="truncate font-bold leading-[1.15]">ntfy</p>
          <p className="truncate text-xs text-sidebar-foreground/55">ntfy.thuthi.kr</p>
        </div>
      </div>
      <nav className={`flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3 pb-3 ${alertVisible ? "pt-0" : "pt-3"}`}>
        {showNotificationPermissionRequired && <NotificationPermissionRequired />}
        {showNotificationPermissionDenied && <NotificationPermissionDeniedAlert />}
        {showNotificationBrowserNotSupportedBox && <NotificationBrowserNotSupportedAlert />}
        {showNotificationContextNotSupportedBox && <NotificationContextNotSupportedAlert />}
        {showNotificationIOSInstallRequired && <NotificationIOSInstallRequiredAlert />}
        {alertVisible && <Separator className="my-2 bg-sidebar-foreground/10" />}
        {!showSubscriptionsList && (
          <button
            type="button"
            onClick={() => handleNavigate(routes.app)}
            className={navItemClasses(location.pathname === config.app_root)}
          >
            <MessageSquare className="size-5 shrink-0" />
            <span className="truncate">{t("nav_button_all_notifications")}</span>
          </button>
        )}
        {showSubscriptionsList && (
          <>
            <div className="mt-2 px-2.5 text-[0.72rem] font-bold uppercase leading-7 tracking-[0.08em] text-sidebar-foreground/40">
              {t("nav_topics_title")}
            </div>
            <button
              type="button"
              onClick={() => handleNavigate(routes.app)}
              className={navItemClasses(location.pathname === config.app_root)}
            >
              <MessageSquare className="size-5 shrink-0" />
              <span className="truncate">{t("nav_button_all_notifications")}</span>
            </button>
            <SubscriptionList
              subscriptions={props.subscriptions}
              selectedSubscription={props.selectedSubscription}
              onMobileDrawerClose={props.onMobileDrawerClose}
            />
            <Separator className="my-2.5 bg-sidebar-foreground/10" />
          </>
        )}
        {session.exists() && (
          <button type="button" onClick={handleAccountClick} className={navItemClasses(location.pathname === routes.account)}>
            <User className="size-5 shrink-0" />
            <span className="truncate">{t("nav_button_account")}</span>
          </button>
        )}
        <button
          type="button"
          onClick={() => handleNavigate(routes.settings)}
          className={navItemClasses(location.pathname === routes.settings)}
        >
          <Settings className="size-5 shrink-0" />
          <span className="truncate">{t("nav_button_settings")}</span>
        </button>
        <button type="button" onClick={handlePublishMessageClick} className={navItemClasses(false)}>
          <Send className="size-5 shrink-0" />
          <span className="truncate">{t("nav_button_publish_message")}</span>
        </button>
        <button type="button" onClick={props.onSubscribeClick} className={navItemClasses(false)}>
          <Plus className="size-5 shrink-0" />
          <span className="truncate">{t("nav_button_subscribe")}</span>
        </button>
      </nav>
    </>
  );
};

interface SubscriptionListProps {
  subscriptions?: Subscription[];
  selectedSubscription?: Subscription | null;
  onMobileDrawerClose?: () => void;
}

const SubscriptionList = (props: SubscriptionListProps) => {
  const sortedSubscriptions = (props.subscriptions ?? [])
    .filter((s) => !s.internal)
    .sort((a, b) => (topicUrl(a.baseUrl, a.topic) < topicUrl(b.baseUrl, b.topic) ? -1 : 1));
  return (
    <>
      {sortedSubscriptions.map((subscription: Subscription) => (
        <SubscriptionItem
          key={subscription.id}
          subscription={subscription}
          selected={props.selectedSubscription && props.selectedSubscription.id === subscription.id}
          onMobileDrawerClose={props.onMobileDrawerClose}
        />
      ))}
    </>
  );
};

interface SubscriptionItemProps {
  subscription: Subscription;
  selected?: boolean | null;
  onMobileDrawerClose?: () => void;
}

const SubscriptionItem = (props: SubscriptionItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { subscription } = props;
  const iconBadge = subscription.new! <= 99 ? subscription.new : "99+";
  const displayName = topicDisplayName(subscription);
  const ariaLabel = subscription.state === ConnectionState.Connecting ? `${displayName} (${t("nav_button_connecting")})` : displayName;
  const icon =
    subscription.state === ConnectionState.Connecting ? (
      <Spinner className="size-5 shrink-0" />
    ) : (
      <span className="relative inline-flex shrink-0">
        {subscription.icon ? <TopicAvatar topic={subscription.topic} icon={subscription.icon} unread className="size-5 rounded-md !ring-0 !opacity-100" /> : <MessageSquare className="size-5" />}
        {subscription.new! > 0 && (
          <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
            {iconBadge}
          </span>
        )}
      </span>
    );

  const handleClick = () => {
    props.onMobileDrawerClose?.();
    navigate(routes.forSubscription(subscription), { replace: routes.isTopLevel(window.location.pathname) });
  };

  // 원본 MUI ListItemButton 은 div 기반이라 내부에 메뉴 버튼(중첩 인터랙션)이 허용됐다.
  // 동일하게 행을 div[role=button] 으로 만들고 SubscriptionPopup 트리거를 인라인 배치한다.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return; // 내부 메뉴 트리거의 키 입력은 행에서 처리하지 않는다
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={ariaLabel}
      aria-live="polite"
      className={navItemClasses(props.selected)}
    >
      {icon}
      <span className={`min-w-0 flex-1 truncate ${props.selected ? "font-bold" : "font-medium"}`}>{displayName}</span>
      {subscription.mutedUntil > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex shrink-0" aria-label={t("nav_button_muted")}>
              <BellOff className="size-4" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{t("nav_button_muted")}</TooltipContent>
        </Tooltip>
      )}
      {/* MUI Menu(anchorEl) 패턴 재구성: 트리거 버튼을 SubscriptionPopup 이 DropdownMenuTrigger(asChild)로 감싼다 */}
      <SubscriptionPopup
        subscription={subscription}
        trigger={
          <Button
            variant="ghost"
            size="icon"
            className="-mr-1 size-7 shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-foreground/15 hover:text-sidebar-foreground"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <EllipsisVertical className="size-4" />
          </Button>
        }
      />
    </div>
  );
};

// 사이드바가 항상 어두워 warning 알림은 amber 계열로 직접 스타일링한다 (shadcn Alert 에는 warning variant 없음)
const navAlertClasses = "mt-2 border-amber-400/30 bg-amber-400/10 text-amber-100";

const NotificationPermissionRequired = () => {
  const { t } = useTranslation();
  const requestPermission = async () => {
    await notifier.maybeRequestPermission();
  };
  return (
    <Alert className={navAlertClasses}>
      <TriangleAlert />
      <AlertTitle>{t("alert_notification_permission_required_title")}</AlertTitle>
      <AlertDescription className="text-amber-100/80">
        <p>{t("alert_notification_permission_required_description")}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={requestPermission}
          className="justify-self-end border-amber-400/30 bg-transparent text-amber-100 hover:bg-amber-400/15 hover:text-amber-50"
        >
          {t("alert_notification_permission_required_button")}
        </Button>
      </AlertDescription>
    </Alert>
  );
};

const NotificationPermissionDeniedAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert className={navAlertClasses}>
      <TriangleAlert />
      <AlertTitle>{t("alert_notification_permission_denied_title")}</AlertTitle>
      <AlertDescription className="text-amber-100/80">
        <p>{t("alert_notification_permission_denied_description")}</p>
      </AlertDescription>
    </Alert>
  );
};

const NotificationIOSInstallRequiredAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert className={navAlertClasses}>
      <TriangleAlert />
      <AlertTitle>{t("alert_notification_ios_install_required_title")}</AlertTitle>
      <AlertDescription className="text-amber-100/80">
        <p>{t("alert_notification_ios_install_required_description")}</p>
      </AlertDescription>
    </Alert>
  );
};

const NotificationBrowserNotSupportedAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert className={navAlertClasses}>
      <TriangleAlert />
      <AlertTitle>{t("alert_not_supported_title")}</AlertTitle>
      <AlertDescription className="text-amber-100/80">
        <p>{t("alert_not_supported_description")}</p>
      </AlertDescription>
    </Alert>
  );
};

const NotificationContextNotSupportedAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert className={navAlertClasses}>
      <TriangleAlert />
      <AlertTitle>{t("alert_not_supported_title")}</AlertTitle>
      <AlertDescription className="text-amber-100/80">
        <p>
          <Trans
            i18nKey="alert_not_supported_context_description"
            components={{
              mdnLink: (
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                <a
                  href="https://developer.mozilla.org/en-US/docs/Web/API/notification"
                  target="_blank"
                  rel="noopener"
                  className="underline underline-offset-4 hover:text-amber-50"
                />
              ),
            }}
          />
        </p>
      </AlertDescription>
    </Alert>
  );
};

export default Navigation;
